<template>
  <div class="chemtracker-module" data-test="outside-vif">
    <div class="messages status sds-assigned-msg">
      <ul>
        <li>SDS assigned to container {{ getContainerId() }}</li>
      </ul>
    </div>

    <fieldset>
      <legend>SDS Information</legend>
      <b-row>
        <b-col>
          <span class="raftjs-entity-view-label">Title: </span>
          {{ getSdsTitle() }}
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <span class="raftjs-entity-view-label">Source: </span>
          {{ getSdsSource() }}
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <span class="raftjs-entity-view-label">Origin: </span>
          {{ getSdsOrigin() }}
        </b-col>
      </b-row>
    </fieldset>

    <p>
      <strong>{{ chemContainersCount }}</strong> other containers of this
      chemical were found. The container already assigned to this SDS is
      excluded. Select and filter results below to assign this SDS to additional
      chemical containers.
    </p>

    <fieldset v-if="foundContainers" class="sds-attachments-filters">
      <legend>Filters</legend>
      <form class="form-inline my-2 my-lg-0">
        <div class="form-group sds-attachment-filter-wrapper">
          <label for="chemical-container-filter-synonym">Chemical Name:</label>
          <b-input
            v-model="sdsChemicalNameFilter"
            type="text"
            id="chemical-container-filter-synonym"
            name="chemical-container-filter-synonym"
            aria-label="Filter by preferred chemical name"
            size="sm"
            maxlength="255"
            placeholder="Filter by chemical name"
          />
        </div>

        <div class="form-group sds-attachment-filter-wrapper">
          <label for="chemical-container-filter-manufacturer"
            >Manufacturer:</label
          >
          <b-input
            v-model="sdsChemicalManufacturerFilter"
            type="text"
            id="chemical-container-filter-manufacturer"
            name="chemical-container-filter-manufacturer"
            aria-label="Filter by manufacturer"
            size="sm"
            maxlength="255"
            placeholder="Filter by manufacturer"
          />
        </div>

        <div class="form-group sds-attachment-filter-wrapper">
          <label for="chemical-container-filter-product">Product Name:</label>
          <b-input
            v-model="sdsChemicalProductFilter"
            type="text"
            id="chemical-container-filter-product"
            name="chemical-container-filter-product"
            aria-label="Filter by product"
            size="sm"
            maxlength="255"
            placeholder="Filter by product"
          />
        </div>

        <div class="form-group sds-attachment-filter-wrapper">
          <label for="chemical-container-filter-prod-number"
            >Product Number:</label
          >
          <b-input
            v-model="sdsProductNumberFilter"
            type="text"
            id="chemical-container-filter-prod-number"
            name="chemical-container-filter-prod-number"
            aria-label="Filter by product number"
            size="sm"
            maxlength="255"
            placeholder="Filter by a product number"
          />
        </div>

        <div class="sds-attachment-filter-button-wrapper">
          <button @click.prevent="getChemicalContainers">Apply</button>
        </div>
      </form>
    </fieldset>

    <b-table
      striped
      hover
      :sticky-header="true"
      :no-local-sorting="true"
      show-empty
      :busy="isBusy"
      :fields="fields"
      :items="chemContainers"
      ref="chemContainersTable"
      :class="{
        'table-pause-scroll': isBusy || chemContainers.length < 1,
      }"
    >
      <template #head(selected)="data">
        <input type="checkbox" v-model="allSelected" /> </template
      >>

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <div v-if="$isB4" class="loading-throbber">
            <b-spinner class="align-middle" />
          </div>
          <div v-else>
            <img
              src="/sites/all/modules/hide_submit/images/squares.gif"
              alt="loading"
            />
          </div>
          <strong>Loading matching chemical containers...</strong>
        </div>
      </template>

      <template #empty>
        <div class="chemtracker-empty-text">
          {{ emptyText }}
        </div>
      </template>

      <template #cell(selected)="row">
        <b-form-checkbox
          @click.native.stop
          :id="'chemical-container-' + row?.item?.id"
          :value="row?.item?.id"
          :checked="allSelected"
          v-model="selectedChemicalContainers"
        />
      </template>
    </b-table>

    <div class="sds-attachments-dialog-controls" v-if="!hideControls">
      <button
        v-if="chemContainersCount > 0"
        @click="sdsBulkAssign"
        id="bulk-assign-btn"
        :disabled="disableSubmit"
      >
        Bulk Assign
      </button>
      <button @click="closeDialog">Exit</button>
    </div>
    <div v-if="hideControls" class="text-center text-danger my-2">
      <div v-if="$isB4" class="loading-throbber">
        <b-spinner class="align-middle" />
      </div>
      <div v-else>
        <img
          src="/sites/all/modules/hide_submit/images/squares.gif"
          alt="loading"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { jsonClient, restClient } from "../../../lib/raftApi";

export default {
  name: "SdsAttachmentsSecondStep",
  inject: ["contextNid"],
  data() {
    return {
      isBusy: false,
      hideControls: false,
      chemContainers: [],
      chemContainersCount: 0,
      foundContainers: false,
      selectedChemicalContainers: [],
      numContainers: 0,
      sdsEntity: {},
      fields: [
        { key: "selected", label: "Select All", sortable: false },
        {
          key: "field_chemical_name_preferred",
          label: "Chemical Name",
          sortable: true,
        },
        {
          key: "field_chemical_container_id",
          label: "Container ID",
          sortable: true,
        },
        {
          key: "field_chemical_manufacturer.name",
          label: "Manufacturer",
          sortable: true,
        },
        {
          key: "field_chemical_product_name",
          label: "Product Name",
          sortable: true,
        },
        {
          key: "field_chemical_product_number",
          label: "Product Number",
          sortable: true,
        },
        {
          key: "field_chemical_note.processed",
          label: "Notes",
          sortable: false,
        },
      ],
      sdsChemicalNameFilter: "",
      sdsChemicalManufacturerFilter: "",
      sdsChemicalProductFilter: "",
      sdsProductNumberFilter: "",
    };
  },
  computed: {
    ...mapGetters([
      "getSelectedSdsEntityUuid",
      "getSelectedSdsEntityType",
      "getSelectedContainer",
    ]),
    disableSubmit() {
      return (
        this.selectedChemicalContainers.length === 0 &&
        this.chemContainers.length > 0
      );
    },
    emptyText() {
      if (this.chemContainersCount == 0) {
        return "No matching chemical containers were found.";
      }
      if (this.chemContainersCount > 50) {
        return (
          "Too many containers to display.  Use filters to narrow down the list to less than 50 results, or " +
          "click the Bulk Assign button below to apply to all matching containers not shown."
        );
      }
      return "";
    },
    allSelected: {
      get() {
        return (
          this.selectedChemicalContainers?.length > 0 &&
          this.selectedChemicalContainers?.length ===
            this.chemContainers?.length
        );
      },
      set(value) {
        return this.selectAll(value);
      },
    },
    filters() {
      if (!this.getSelectedContainer) {
        return;
      }
      const filters = {
        include: "field_chemical_type, field_chemical_manufacturer",
        "fields[chemdb_manufacturer--chemdb_manufacturer]": "name",
        "fields[node--chemical_container]":
          "field_chemical_type,field_chemical_manufacturer,field_chemical_name_preferred,field_chemical_product_name,field_chemical_container_id,field_chemical_product_number",
        "fields[node--chemdb_type]": "id,title,field_chemdb_synonyms",
        "filter[container][condition][path]": "id",
        "filter[container][condition][value]": this.getSelectedContainer.id,
        "filter[container][condition][operator]": "<>",
        "filter[chemical][condition][path]": "field_chemical_type.id",
        "filter[chemical][condition][value]":
          this.getSelectedContainer.field_chemical_type.id,
        "filter[operative_status][condition][path]": "operative_status",
        "filter[operative_status][condition][value]": 1,
      };

      const sdsChemicalSynonymFilter = this.sdsChemicalNameFilter.trim();
      if (sdsChemicalSynonymFilter.length) {
        filters["filter[synonym][condition][path]"] =
          "field_chemical_name_preferred";
        filters["filter[synonym][condition][value]"] = sdsChemicalSynonymFilter;
        filters["filter[synonym][condition][operator]"] = "CONTAINS";
      }

      const sdsChemicalManufacturerFilter =
        this.sdsChemicalManufacturerFilter.trim();
      if (sdsChemicalManufacturerFilter.length) {
        filters["filter[manufacturer][condition][path]"] =
          "field_chemical_manufacturer.name";
        filters["filter[manufacturer][condition][value]"] =
          sdsChemicalManufacturerFilter;
        filters["filter[manufacturer][condition][operator]"] = "CONTAINS";
      }

      const sdsChemicalProductFilter = this.sdsChemicalProductFilter.trim();
      if (sdsChemicalProductFilter) {
        filters["filter[product][condition][path]"] =
          "field_chemical_product_name";
        filters["filter[product][condition][value]"] = sdsChemicalProductFilter;
        filters["filter[product][condition][operator]"] = "CONTAINS";
      }

      const sdsProductNumberFilter = this.sdsProductNumberFilter.trim();
      if (sdsProductNumberFilter) {
        filters["filter[field_chemical_product_number]"] =
          sdsProductNumberFilter;
      }
      return filters;
    },
  },
  methods: {
    ...mapActions([
      "updateShowSdsModal",
      "updateSdsModalTitle",
      "updateSdsBulkAssignStatus",
    ]),
    selectAll(value) {
      this.selectedChemicalContainers = [];
      if (value) {
        for (let i = 0, len = this.chemContainers.length; i < len; i++) {
          this.selectedChemicalContainers.push(this.chemContainers[i].id);
        }
      }
    },
    getContainerId() {
      const container = this.getSelectedContainer;
      if (container) {
        return container.field_chemical_container_id;
      }
      return "";
    },
    getSdsTitle() {
      if (
        this.sdsEntity &&
        this.sdsEntity.hasOwnProperty("field_description")
      ) {
        return this.sdsEntity.field_description;
      }
      return "";
    },
    getSdsSource() {
      if (this.sdsEntity && this.sdsEntity.hasOwnProperty("field_source")) {
        return this.sdsEntity.field_source;
      }
      return "";
    },
    getSdsOrigin() {
      if (this.sdsEntity && this.sdsEntity.hasOwnProperty("type")) {
        switch (this.sdsEntity.type.replace(/raft_metadata--/, "")) {
          case "sds_external":
            return "Centralized Database";
          case "sds_file":
            return "Local Upload";
          case "sds_url":
            return "Url";
          default:
            return "Unknown";
        }
      }
      return "";
    },
    async loadSdsEntity() {
      const filters = {};
      filters["fields[raft_metadata--" + this.getSelectedSdsEntityType + "]"] =
        "field_description,field_source";
      const options = {
        path:
          "raft_metadata/" +
          this.getSelectedSdsEntityType +
          "/" +
          this.getSelectedSdsEntityUuid,
        queries: filters,
      };
      const response = await jsonClient.get(options).catch((error) => {
        const status = {
          title: error.message,
          type: "error",
        };
        this.updateSdsBulkAssignStatus(status);
        this.closeDialog();
        /* eslint-disable no-console */
        console.error(error);
      });
      this.sdsEntity = response?.deserialized;
    },
    async getChemicalContainers() {
      this.selectedChemicalContainers = [];
      this.isBusy = true;
      this.chemContainers = [];
      const queries = this.filters;
      if (this.contextNid) {
        queries["rog-type"] = "node";
        queries["rog-id"] = this.contextNid;
      }
      const options = {
        path: "node/chemical_container",
        queries,
      };
      const response = await jsonClient.get(options).catch((error) => {
        const status = {
          title: error.message,
          type: "error",
        };
        this.updateSdsBulkAssignStatus(status);
        this.closeDialog();
        /* eslint-disable no-console */
        console.error(error);
      });
      this.isBusy = false;
      if (response && response?.deserialized) {
        this.chemContainersCount = response.meta.count;
        if (this.chemContainersCount <= 50) {
          this.chemContainers = response.deserialized;
          this.selectAll(true);
        }
        if (this.chemContainersCount > 0) {
          // If we find containers in the initial search, don't hide the
          // filters if a later search returns 0.
          this.foundContainers = true;
        }
      }
    },
    bulkAssignData() {
      const data = {};
      if (this.chemContainers.length === 0) {
        const preferredNameFilter = this.sdsChemicalNameFilter.trim();
        const manufacturerFilter = this.sdsChemicalManufacturerFilter.trim();
        const productFilter = this.sdsChemicalProductFilter.trim();
        const prodNumFilter = this.sdsProductNumberFilter.trim();
        if (preferredNameFilter) {
          data.field_chemical_name_preferred = preferredNameFilter;
        }
        if (manufacturerFilter) {
          data.field_chemical_manufacturer = manufacturerFilter;
        }
        if (productFilter) {
          data.field_chemical_product_name = productFilter;
        }
        if (prodNumFilter) {
          data.field_chemical_product_number = prodNumFilter;
        }
      } else {
        data.container_ids = this.selectedChemicalContainers;
      }
      return data;
    },
    async sdsBulkAssign() {
      this.isBusy = true;
      this.hideControls = true;
      let totalCount = this.chemContainersCount;
      // We might not have any selected chemical containers, i.e. if there were more than 50 results found and we
      // want to bulk assign to them all.
      if (this.chemContainers && this.chemContainers.length) {
        totalCount = this.selectedChemicalContainers.length;
      }
      const status = {
        title:
          "Successfully bulk-assigned the SDS to " +
          totalCount +
          " chemical containers.  Please allow time for this change to be queued and completed.",
        type: "success",
      };
      const options = {
        path: "chemtracker-sds-bulk-assign/" + this.getSelectedContainer.id,
        queries: {},
        data: this.bulkAssignData(),
      };
      const response = await restClient.post(options).catch((error) => {
        status.title =
          "Failed to bulk-assigned the SDS to " +
          totalCount +
          " chemical containers. " +
          error.message;
        status.type = "error";
        /* eslint-disable no-console */
        console.error(error);
      });
      this.isBusy = false;
      this.updateSdsBulkAssignStatus(status);
      this.closeDialog();
    },
    closeDialog() {
      // Reset data members.
      // @todo Do we need this reset?
      this.sdsChemicalNameFilter = "";
      this.sdsChemicalManufacturerFilter = "";
      this.sdsChemicalProductFilter = "";
      this.chemContainersCount = 0;
      this.foundContainers = false;
      this.allSelected = false;
      this.hideControls = false;
      this.chemContainers = [];
      this.selectedChemicalContainers = [];
      this.numContainers = 0;
      this.sdsEntity = {};
      // Close the dialog.
      this.updateShowSdsModal(false);
    },
  },
  mounted() {
    this.updateSdsModalTitle("Bulk Assign SDS to Similar Containers");
    this.loadSdsEntity();
    this.getChemicalContainers();
  },
};
</script>

<style scoped></style>
